import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LogOut, UtensilsCrossed, ExternalLink } from "lucide-react";
import { ChangePassword } from "@/components/common/PasswordDialogs";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common/StatusBadge";

export function DashboardLayout({ nav, title, children, publicSlug }) {
  const { user, restaurant, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      {/* mobile topbar */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
        <Link to="/" className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500 text-white">
            <UtensilsCrossed className="h-4 w-4" />
          </span>
          Smart Menu
        </Link>
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className="rounded-lg p-2 text-slate-600">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* sidebar */}
      <aside
        className={`${open ? "block" : "hidden"} w-full shrink-0 border-r border-slate-200 bg-white lg:block lg:w-64 lg:min-h-screen`}
      >
        <div className="sticky top-0 flex h-full flex-col">
          <div className="hidden items-center gap-2 px-6 py-5 lg:flex">
            <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-slate-900">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500 text-white">
                <UtensilsCrossed className="h-5 w-5" />
              </span>
              Smart Menu
            </Link>
          </div>

          {restaurant && (
            <div className="mx-4 mb-3 rounded-xl bg-slate-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate font-heading text-sm font-semibold text-slate-800">{restaurant.name}</p>
                <StatusBadge status={restaurant.status} />
              </div>
              {publicSlug && restaurant.status === "active" && (
                <a
                  href={`/r/${restaurant.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="view-public-menu-link"
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:underline"
                >
                  View public menu <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}

          <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4 sm-scroll">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                    isActive
                      ? "bg-emerald-50 text-emerald-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                <item.icon className="h-[18px] w-[18px]" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="border-t border-slate-100 p-3">
            <div className="mb-2 px-2">
              <p className="truncate text-sm font-semibold text-slate-800">{user?.name}</p>
              <p className="truncate text-xs text-slate-400">{user?.email}</p>
            </div>
            <ChangePassword />
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-rose-50 hover:text-rose-600"
            >
              <LogOut className="h-[18px] w-[18px]" /> Sign out
            </button>
          </div>
        </div>
      </aside>

      <main key={loc.pathname} className="sm-fade-up flex-1 overflow-x-hidden px-4 py-6 sm:px-8 sm:py-8">
        <div className="mx-auto max-w-6xl">{children}</div>
      </main>
    </div>
  );
}
